import { ICopyWithBusinessRef } from '../../contracts/copy-with-business-ref.js';
import { VersionRange } from '../../models/core/version-range.js';
import {
  DataModelBuilder, isSameData, isSameList, SerializationContext, SerializedData } from '../../models/dto/dto.js';
import { BusinessModuleRef } from '../../models/serializable/business-module-ref.js';
import { LocalizedString } from '../../models/serializable/localized-string.js';
import { ProfileElementRef } from '../../models/serializable/profile-element-ref.js';
import { MapUtils } from '../../utility/map-utils.js';
import { RenderableComponent } from '../core/component.js';

export class ProfileElementListComponent extends RenderableComponent implements ICopyWithBusinessRef {
  static readonly TYPE_ID: string = '8333d816c85345c28ec19c9671a03a4d';
  static readonly SCHEMA_VERSION: number = 2;

  constructor(
    public readonly titles: LocalizedString,
    public readonly elements: ProfileElementRef[],
    public readonly rendererId: string | null,
    public readonly renderOrder: number,
    public readonly isRenderingEnabled: boolean,
    public readonly allowOpeningItems: boolean,
  ) {
    super();
  }

  get $typeId(): string {
    return ProfileElementListComponent.TYPE_ID;
  }

  get $removable(): boolean {
    return true;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, ProfileElementListComponent.SCHEMA_VERSION);
  }

  static fromMap(context: SerializationContext, map: SerializedData): ProfileElementListComponent {
    return new ProfileElementListComponent(
        context.fromData<LocalizedString>(MapUtils.get<SerializedData>(map, 'titles')),
        MapUtils.getList<ProfileElementRef>(
            map,
            'elements',
            (e) => context.fromData<ProfileElementRef>(e as SerializedData),
        ),
        MapUtils.getOrNull<string>(map, 'rendererId'),
        MapUtils.get<number>(map, 'renderOrder'),
        MapUtils.get<boolean>(map, 'isRenderingEnabled'),
        MapUtils.get<boolean>(map, 'allowOpeningItems'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'titles': context.toData(this.titles),
      'elements': this.elements.map((e) => context.toData(e)),
      'rendererId': this.rendererId,
      'renderOrder': this.renderOrder,
      'isRenderingEnabled': this.isRenderingEnabled,
      'allowOpeningItems': this.allowOpeningItems,
    };
  }

  clone(): this {
    return new ProfileElementListComponent(
        this.titles.clone(),
        this.elements.map((e) => e.clone()),
        this.rendererId,
        this.renderOrder,
        this.isRenderingEnabled,
        this.allowOpeningItems,
    ) as this;
  }

  isSame(other: ProfileElementListComponent): boolean {
    if(this === other) {
      return true;
    }

    return isSameData(other.titles, this.titles) &&
      isSameList(other.elements, this.elements) &&
      other.rendererId === this.rendererId &&
      other.renderOrder === this.renderOrder &&
      other.isRenderingEnabled === this.isRenderingEnabled &&
      other.allowOpeningItems === this.allowOpeningItems;
  }

  copyWithBusinessRef(businessRef: BusinessModuleRef): this {
    return new ProfileElementListComponent(
        this.titles.clone(),
        this.elements.map((e) => e.copyWithBusinessRef(businessRef)),
        this.rendererId,
        this.renderOrder,
        this.isRenderingEnabled,
        this.allowOpeningItems,
    ) as this;
  }
}

export class ProfileElementListComponentBuilder extends DataModelBuilder<ProfileElementListComponent> {
  build(context: SerializationContext, map: SerializedData): ProfileElementListComponent {
    return ProfileElementListComponent.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(ProfileElementListComponent.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(ProfileElementListComponent.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // Version 2:
    // - adds [allowOpeningItems] field
    if(version < 2) {
      MapUtils.setIfNullOrUndefined(map, 'allowOpeningItems', true);

      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
